import apiClient from './client';

// ══════════════════════════════════════════════
// Encompass Types
// ══════════════════════════════════════════════

export interface EncompassTransaction {
  id: string;
  status: string;
  partnerStatus?: string;
  clientId?: string;
  request?: {
    loan?: Record<string, unknown>;
    options?: Record<string, unknown>;
  };
  response?: {
    status?: string;
    partnerStatus?: string;
    errors?: Array<{ code: string; message: string }>;
    resources?: Array<{
      id: string;
      name: string;
      mimeType?: string;
      size?: number;
    }>;
  };
  events?: Array<{
    id: string;
    type: string;
    date: string;
    message?: string;
  }>;
}

export interface EncompassResource {
  id: string;
  name: string;
  mimeType?: string;
  size?: number;
  url?: string;
}

export interface EncompassLoan {
  loanId: string;
  borrowerFirstName?: string;
  borrowerLastName?: string;
  propertyAddress?: string;
  propertyCity?: string;
  propertyState?: string;
  propertyZip?: string;
  loanAmount?: number;
  loanPurpose?: string;
  loanType?: string;
  interestRate?: number;
  [key: string]: unknown;
}

export interface EncompassActivity {
  type: string;
  message: string;
  date: string;
  correlationId?: string;
  [key: string]: unknown;
}

// ══════════════════════════════════════════════
// MeridianLink Types
// ══════════════════════════════════════════════

export interface MeridianLinkLoan {
  loanNumber: string;
  applicants?: Array<{
    firstName?: string;
    lastName?: string;
    ssn?: string;
    email?: string;
    phone?: string;
  }>;
  property?: {
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    county?: string;
    type?: string;
    value?: number;
  };
  loanInfo?: {
    loanAmount?: number;
    loanPurpose?: string;
    loanType?: string;
    interestRate?: number;
    term?: number;
    ltv?: number;
  };
  comments?: Array<{
    date?: string;
    text?: string;
    author?: string;
  }>;
  [key: string]: unknown;
}

export interface MeridianLinkDocument {
  id?: string;
  title: string;
  fileName: string;
  docGroup?: string;
  docCode?: string;
  uploadDate?: string;
  status?: string;
}

export interface MeridianLinkUpdateResponse {
  success: boolean;
  status: 'COMPLETE' | 'DEFERRED' | 'FAILED';
  message?: string;
}

// ══════════════════════════════════════════════
// Encompass API Functions
// ══════════════════════════════════════════════

export async function getEncompassTransaction(transactionId: string): Promise<EncompassTransaction> {
  const response = await apiClient.get(`/los/encompass/transactions/${transactionId}`);
  return response.data;
}

export async function getEncompassTransactionResources(transactionId: string): Promise<EncompassResource[]> {
  const response = await apiClient.get(`/los/encompass/transactions/${transactionId}/resources`);
  return response.data;
}

export async function forceEncompassStatus(
  transactionId: string,
  body: { status: string; partnerStatus?: string; reason?: string },
): Promise<any> {
  const response = await apiClient.post(`/los/encompass/transactions/${transactionId}/force-status`, body);
  return response.data;
}

export async function getEncompassLoan(loanId: string): Promise<EncompassLoan> {
  const response = await apiClient.get(`/los/encompass/loans/${loanId}`);
  return response.data;
}

export async function uploadEncompassDocument(
  transactionId: string,
  body: { fileName: string; contentType: string; content: string },
): Promise<any> {
  const response = await apiClient.post(`/los/encompass/transactions/${transactionId}/documents`, body);
  return response.data;
}

export async function getEncompassActivities(correlationId: string): Promise<EncompassActivity[]> {
  const response = await apiClient.get('/los/encompass/activities', { params: { correlationId } });
  return response.data;
}

// ══════════════════════════════════════════════
// MeridianLink API Functions
// ══════════════════════════════════════════════

export async function getMeridianLinkLoan(loanNumber: string): Promise<MeridianLinkLoan> {
  const response = await apiClient.get(`/los/meridianlink/loans/${loanNumber}`);
  return response.data;
}

export async function updateMeridianLinkLoanFragment(
  loanNumber: string,
  body: { commands: any[] },
): Promise<MeridianLinkUpdateResponse> {
  const response = await apiClient.post(`/los/meridianlink/loans/${loanNumber}/fragments`, body);
  return response.data;
}

export async function submitMeridianLinkDocument(
  appNumber: string,
  body: { title: string; fileName: string; docGroup?: string; docCode?: string; content: string },
): Promise<any> {
  const response = await apiClient.post(`/los/meridianlink/loans/applications/${appNumber}/documents`, body);
  return response.data;
}

export async function getMeridianLinkDocuments(
  loanNumber: string,
  params?: { appType?: string; documentTypes?: string },
): Promise<MeridianLinkDocument[]> {
  const response = await apiClient.get(`/los/meridianlink/loans/${loanNumber}/documents`, { params });
  return response.data;
}
